import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import {forkJoin, of} from 'rxjs';
import {catchError, mergeMap} from 'rxjs/operators';
import { GetEmpty } from 'src/app/classes/Empties';
import {ItemInfo, ItemOptionInfo, ItemOptionValueInfo} from 'src/app/models/models';
import {Category, Item, Item_Exception, Item_Option, Price, Price_List, Price_Type, Stock_Record, Unidad_Medida_Sat} from 'src/app/models/RestModels';
import {BaseComponent} from 'src/app/pages/base/base.component';

@Component({
	selector: 'app-add-new-item-easy',
	templateUrl: './add-new-item-easy.component.html',
	styleUrls: ['./add-new-item-easy.component.css']
})
export class AddNewItemEasyComponent extends BaseComponent
{
	@Output() newItem = new EventEmitter<ItemInfo | null>();
	@Input() type:string = 'PRODUCT';
	@Input() initial_barcode:string = '';

	item_info:ItemInfo = GetEmpty.item_info();

	category_list: Category[];
	unidad_medida_sat_list: Unidad_Medida_Sat[];

	price_list_list:Price_List[] = [];
	price_type_list:Price_Type[] = [];
	price_dict:Record<string, Price> = {};

	//Copiar configuración de otro artículo
	show_copy_item:boolean = false;
	copy_search_str:string = '';
	copy_prices:boolean = true;
	copied_from:ItemInfo | null = null;
	selected_category_id:number | null = null;
	selected_category_name:string = '';

	ngOnChanges(changes: SimpleChanges): void 
	{
		if (changes['initial_barcode'] && this.initial_barcode)
		{
			if (this.item_info && this.item_info.item)
			{
				this.item_info.item.code = this.initial_barcode;
			}
		}
	}

	ngOnInit(): void
	{
		this.subs.sink = forkJoin
		({
			categories: this.rest.category.search({eq:{type: this.type},sort_order:['name_ASC'], limit:9999}),
			price_list: this.rest.getPriceLists(true),
			price_type: this.rest.getPriceTypes(true),
			unidad_medida_sat: this.rest.unidad_medida_sat.search
			({
				limit:9999,
				sort_order:['nombre_ASC']
			})
		})
		.subscribe((response)=>
		{
			this.resetValues();
			this.initPriceLists( response.price_list.data, response.price_type.data );
			this.price_list_list = response.price_list.data;
			this.price_type_list = response.price_type.data;
			this.category_list = response.categories.data;
			this.unidad_medida_sat_list = response.unidad_medida_sat.data;
		})
	}

	initPriceLists(pl:Price_List[], pt:Price_Type[])
	{
		this.price_dict = {};
		pl.forEach(price_list=>
		{
			pt.forEach(price_type=>
			{
				let key:string = price_list.id+'-'+price_type.id

				let iprice = this.item_info.item.reference_price || 1;

				this.price_dict[ key ] = {
					price_list_id 	: price_list.id,
          			updated: new Date(),
					price_type_id	: price_type.id,
					item_id			: this.item_info.item.id,
					price			: 0,
					percent 		: 0,
					tax_included	: 'NO',
					currency_id		: 'MXN'
				};
			});
		});
	}

	resetValues()
	{
		this.item_info = GetEmpty.item_info();
		this.item_info.item.availability_type =this.rest.local_preferences?.default_pos_availability_type || 'ALWAYS';
		if (this.initial_barcode) {
			this.item_info.item.code = this.initial_barcode;
		}

		this.copied_from = null;
		this.copy_search_str = '';
		this.show_copy_item = false;
		this.selected_category_id = null;
		this.selected_category_name = '';
	}

	showCopyItemModal()
	{
		this.copy_search_str = '';
		this.show_copy_item = true;
	}

	onCopyItemSelected(item_info:ItemInfo)
	{
		if( !item_info || !item_info.item )
			return;

		this.show_copy_item = false;
		this.is_loading = true;

		this.subs.sink = this.rest.item_info.get( item_info.item.id )
		.subscribe
		({
			next:(response)=>
			{
				this.is_loading = false;
				this.applyCopy( response );
			},
			error:(_error)=>
			{
				this.is_loading = false;
				this.showError('Ocurrio un error al cargar la información del artículo, por favor intente de nuevo');
			}
		});
	}

	applyCopy(source:ItemInfo)
	{
		//Se copia toda la configuración menos el código y el nombre
		//El backend ignora id,created,updated y los usuarios al insertar
		let item:Item = {...source.item};
		item.id = 0;
		item.code = this.initial_barcode || '';
		item.name = '';

		this.item_info.item = item;
		this.item_info.exceptions = (source.exceptions || []).map((ie:Item_Exception)=>
		{
			return {...ie, id: 0 };
		});

		this.item_info.options = (source.options || []).map((ioi:ItemOptionInfo)=>
		{
			let values:ItemOptionValueInfo[] = ioi.values.map((iovi:ItemOptionValueInfo)=>
			{
				return {
					item: iovi.item,
					item_option_value:
					{
						item_id			: iovi.item_option_value.item_id,
						max_extra_qty	: iovi.item_option_value.max_extra_qty,
						extra_price		: iovi.item_option_value.extra_price,
						charge_type		: iovi.item_option_value.charge_type,
						price			: iovi.item_option_value.price
					}
				} as ItemOptionValueInfo;
			});

			let item_option:Item_Option = {
				item_id				: ioi.item_option.item_id,
				name				: ioi.item_option.name,
				included_options	: ioi.item_option.included_options,
				max_options			: ioi.item_option.max_options,
				min_options			: ioi.item_option.min_options,
				min_selections		: ioi.item_option.min_selections,
				included_extra_qty	: ioi.item_option.included_extra_qty,
				max_extra_qty		: ioi.item_option.max_extra_qty
			};

			return { item_option, values } as ItemOptionInfo;
		});

		this.copied_from = source;

		this.selected_category_id = item.category_id;
		this.selected_category_name = source.category ? source.category.name : '';

		if( this.copy_prices )
		{
			this.copyPricesFrom( source );
		}
	}

	copyPricesFrom(source:ItemInfo)
	{
		let prices = source.prices || [];

		prices.forEach((p:Price)=>
		{
			let key = p.price_list_id+'-'+p.price_type_id;

			if( !this.price_dict[key] )
				return;

			this.price_dict[key].price = p.price;
			this.price_dict[key].percent = p.percent;
			this.price_dict[key].tax_included = p.tax_included;
			this.price_dict[key].currency_id = p.currency_id;
		});
	}

	clearCopy()
	{
		let code = this.item_info.item.code;
		let name = this.item_info.item.name;
		let image_id = this.item_info.item.image_id;

		this.resetValues();
		this.item_info.item.code = code;
		this.item_info.item.name = name;
		this.item_info.item.image_id = image_id;
		this.initPriceLists( this.price_list_list, this.price_type_list );
	}

	save(evt:Event)
	{
		this.subs.sink	= this.rest.item_info.create
		({
			item: this.item_info.item,
			options: this.item_info.options,
			exceptions: this.item_info.exceptions
		})
		.pipe
		(
			mergeMap((response)=>
			{
				let price_array:Price[]= [];

				this.price_type_list.forEach((pt)=>
				{
					this.price_list_list.forEach((pl:Price_List)=>
					{
						let k = pl.id+'-'+pt.id;
						let p = this.price_dict[k];

						if( p.price>0 )
						{
							if( pt.tax_model != 'ALL' )
							{
								p.tax_included = pt.tax_model == 'TAX_INCLUDED' ? 'YES' : 'NO';
							}
							p.price = p.price;
							p.item_id = response.item.id;

							price_array.push( p );
						}
					});
				});

				return this.rest.price
				.batchCreate(price_array).pipe
				(
					mergeMap(()=>
					{
						return of( response );
					}),
					catchError((_error)=>
					{
						return of( response );
					})
				);
			})
		)
		.subscribe((item_info)=>
		{
			this.is_loading = false;

			try{
				this.showSuccess('El artículo se guardó exitosamente');
				let formElement = evt.target as HTMLFormElement;
				this.resetValues();
				this.initPriceLists( this.price_list_list, this.price_type_list );
				formElement.reset();
			}
			catch(e)
			{

			}

			this.newItem.emit(item_info);
		},(error)=>this.showError(error));
	}
}
